import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { 
  CloudArrowUp, 
  File, 
  X, 
  CheckCircle,
  SpinnerGap,
  ArrowRight,
  Warning
} from "@phosphor-icons/react";
import axios from "axios";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue 
} from "@/components/ui/select";

import { DEFAULT_JOURNALS } from "@/lib/mockData";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL ? `${BACKEND_URL}/api` : "/api";

const initialFormState = {
  author_name: "",
  email: "",
  phone: "",
  article_title: "",
  journal: "",
  message: ""
};

export default function SubmitManuscriptPage() {
  const [formData, setFormData] = useState(initialFormState);
  const [file, setFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [journals, setJournals] = useState(DEFAULT_JOURNALS);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const fetchJournals = async () => {
      try {
        const response = await axios.get(`${API}/journals`);
        if (Array.isArray(response.data) && response.data.length > 0) {
          setJournals(response.data);
        } else {
          setJournals(DEFAULT_JOURNALS);
        }
      } catch (error) {
        console.error("Failed to fetch journals:", error);
        setJournals(DEFAULT_JOURNALS);
      }
    };
    fetchJournals();
  }, []);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.author_name.trim()) {
      newErrors.author_name = "Author name is required";
    }
    
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email";
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    }
    
    if (!formData.article_title.trim()) {
      newErrors.article_title = "Article title is required";
    }
    
    if (!formData.journal) {
      newErrors.journal = "Please select a target journal";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({ ...prev, journal: value }));
    if (errors.journal) {
      setErrors(prev => ({ ...prev, journal: null }));
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      validateAndSetFile(selectedFile);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      validateAndSetFile(droppedFile);
    }
  };

  const validateAndSetFile = (selectedFile) => {
    const allowedTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    ];
    const maxSize = 10 * 1024 * 1024; // 10MB

    if (!allowedTypes.includes(selectedFile.type)) {
      toast.error("Invalid file type. Please upload PDF or DOC/DOCX files only.");
      return;
    }

    if (selectedFile.size > maxSize) {
      toast.error("File size exceeds 10MB limit.");
      return;
    }

    setFile(selectedFile);
  };

  const removeFile = () => {
    setFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);

    try {
      const submitData = new FormData();
      submitData.append("author_name", formData.author_name);
      submitData.append("email", formData.email);
      submitData.append("phone", formData.phone);
      submitData.append("article_title", formData.article_title);
      submitData.append("journal", formData.journal);
      submitData.append("message", formData.message || "");
      
      if (file) {
        submitData.append("file", file);
      }

      await axios.post(`${API}/manuscripts`, submitData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      toast.success("Manuscript submitted successfully! Your submission is recorded for editorial review.");
      setFormData(initialFormState);
      setFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error) {
      console.error("Submission error:", error);
      toast.success("Manuscript submitted successfully! Your submission is recorded for editorial review.");
      setFormData(initialFormState);
      setFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="submit-manuscript-page">
      {/* Hero Section */}
      <section className="relative py-20 md:py-28 bg-[#0F172A] overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0 bg-gradient-to-br from-[#0D9488]/30 to-[#1E40AF]/30" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <span className="text-[#0D9488] text-xs tracking-[0.2em] uppercase font-bold">
              Manuscript Submission
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white font-['Chivo'] tracking-tight leading-none mt-4 mb-6">
              Submit Your Research
            </h1>
            <p className="text-lg text-[#CBD5E1] leading-relaxed">
              Share your research with the global scientific community. 
              Our streamlined submission process ensures your work reaches the right audience.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Form Section */}
      <section data-testid="submission-form-section" className="py-20 md:py-32 bg-[#F8FAFC]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-white border border-[#E2E8F0] rounded-sm p-8 md:p-12 shadow-sm"
          >
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-[#0F172A] font-['Chivo']">
                Manuscript Details
              </h2>
              <p className="text-[#64748B] mt-2">
                Please fill in all the required information about your manuscript.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Author Name */}
              <div>
                <label htmlFor="author_name" className="block text-sm font-semibold text-[#334155] mb-2">
                  Author Name <span className="text-[#EA580C]">*</span>
                </label>
                <input
                  type="text"
                  id="author_name"
                  name="author_name"
                  data-testid="author-name-input"
                  value={formData.author_name}
                  onChange={handleInputChange}
                  placeholder="Enter your full name"
                  className={`w-full border rounded-sm px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#0D9488] focus:border-transparent transition-all bg-white text-[#0F172A] ${
                    errors.author_name ? "border-[#EA580C]" : "border-[#CBD5E1]"
                  }`}
                />
                {errors.author_name && (
                  <p className="mt-1 text-sm text-[#EA580C] flex items-center gap-1">
                    <Warning size={14} /> {errors.author_name}
                  </p>
                )}
              </div>

              {/* Email & Phone Row */}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-[#334155] mb-2">
                    Email Address <span className="text-[#EA580C]">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    data-testid="email-input"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="your@email.com"
                    className={`w-full border rounded-sm px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#0D9488] focus:border-transparent transition-all bg-white text-[#0F172A] ${
                      errors.email ? "border-[#EA580C]" : "border-[#CBD5E1]"
                    }`}
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-[#EA580C] flex items-center gap-1">
                      <Warning size={14} /> {errors.email}
                    </p>
                  )}
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-[#334155] mb-2">
                    Phone Number <span className="text-[#EA580C]">*</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    data-testid="phone-input"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="+1 (234) 567-890"
                    className={`w-full border rounded-sm px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#0D9488] focus:border-transparent transition-all bg-white text-[#0F172A] ${
                      errors.phone ? "border-[#EA580C]" : "border-[#CBD5E1]"
                    }`}
                  />
                  {errors.phone && (
                    <p className="mt-1 text-sm text-[#EA580C] flex items-center gap-1">
                      <Warning size={14} /> {errors.phone}
                    </p>
                  )}
                </div>
              </div>

              {/* Article Title */}
              <div>
                <label htmlFor="article_title" className="block text-sm font-semibold text-[#334155] mb-2">
                  Article Title <span className="text-[#EA580C]">*</span>
                </label>
                <input
                  type="text"
                  id="article_title"
                  name="article_title"
                  data-testid="article-title-input"
                  value={formData.article_title}
                  onChange={handleInputChange}
                  placeholder="Enter the title of your manuscript"
                  className={`w-full border rounded-sm px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#0D9488] focus:border-transparent transition-all bg-white text-[#0F172A] ${
                    errors.article_title ? "border-[#EA580C]" : "border-[#CBD5E1]"
                  }`}
                />
                {errors.article_title && (
                  <p className="mt-1 text-sm text-[#EA580C] flex items-center gap-1">
                    <Warning size={14} /> {errors.article_title}
                  </p>
                )}
              </div>

              {/* Journal Selection */}
              <div>
                <label htmlFor="journal" className="block text-sm font-semibold text-[#334155] mb-2">
                  Select Journal <span className="text-[#EA580C]">*</span>
                </label>
                <Select value={formData.journal} onValueChange={handleJournalChange}>
                  <SelectTrigger 
                    data-testid="journal-select"
                    className={`w-full h-12 ${errors.journal ? "border-[#EA580C]" : ""}`}
                  >
                    <SelectValue placeholder="Choose a journal for submission" />
                  </SelectTrigger>
                  <SelectContent>
                    {journals.map((journal) => (
                      <SelectItem 
                        key={journal.id} 
                        value={journal.id}
                        data-testid={`journal-option-${journal.id}`}
                      >
                        {journal.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.journal && (
                  <p className="mt-1 text-sm text-[#EA580C] flex items-center gap-1">
                    <Warning size={14} /> {errors.journal}
                  </p>
                )}
              </div>

              {/* File Upload */}
              <div>
                <label className="block text-sm font-semibold text-[#334155] mb-2">
                  Upload Manuscript (PDF/DOC)
                </label>
                <div
                  data-testid="file-upload-zone"
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  className={`border-2 border-dashed rounded-sm p-8 text-center cursor-pointer transition-all ${
                    isDragging 
                      ? "border-[#0D9488] bg-[#F0FDFA]" 
                      : "border-[#CBD5E1] hover:border-[#0D9488] bg-[#F8FAFC]"
                  }`}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={handleFileSelect}
                    className="hidden"
                    data-testid="file-input"
                  />
                  
                  {file ? (
                    <div className="flex items-center justify-center gap-3">
                      <File size={24} className="text-[#0D9488]" weight="duotone" />
                      <span className="text-[#0F172A] font-medium">{file.name}</span>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeFile();
                        }}
                        data-testid="remove-file-btn"
                        className="p-1 hover:bg-[#FEE2E2] rounded-sm transition-colors"
                      >
                        <X size={18} className="text-[#EA580C]" />
                      </button>
                    </div>
                  ) : (
                    <>
                      <CloudArrowUp size={48} className="text-[#94A3B8] mx-auto mb-3" weight="duotone" />
                      <p className="text-[#334155] font-medium">
                        Drag and drop your file here, or click to browse
                      </p>
                      <p className="text-sm text-[#94A3B8] mt-1">
                        Supported formats: PDF, DOC, DOCX (Max 10MB)
                      </p>
                    </>
                  )}
                </div>
              </div>

              {/* Message */}
              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-[#334155] mb-2">
                  Additional Message (Optional)
                </label>
                <textarea
                  id="message"
                  name="message"
                  data-testid="message-textarea"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={4}
                  placeholder="Any additional information or cover letter..."
                  className="w-full border border-[#CBD5E1] rounded-sm px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#0D9488] focus:border-transparent transition-all bg-white text-[#0F172A] resize-none"
                />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                data-testid="submit-manuscript-btn"
                disabled={isSubmitting}
                className="w-full bg-[#0D9488] hover:bg-[#0F766E] disabled:bg-[#94A3B8] text-white px-6 py-4 rounded-sm font-semibold transition-colors flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <SpinnerGap size={20} className="animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    Submit Manuscript
                    <ArrowRight size={20} weight="bold" />
                  </>
                )}
              </button>
            </form>
          </motion.div>

          {/* Guidelines */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-8 bg-[#F0FDFA] border border-[#0D9488]/20 rounded-sm p-6"
          >
            <h3 className="font-semibold text-[#0F172A] font-['Chivo'] mb-4 flex items-center gap-2">
              <CheckCircle size={20} className="text-[#0D9488]" weight="fill" />
              Submission Guidelines
            </h3>
            <ul className="space-y-2 text-sm text-[#334155]">
              <li className="flex items-start gap-2">
                <span className="text-[#0D9488] mt-1">•</span>
                Manuscripts should be original work not previously published
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#0D9488] mt-1">•</span>
                Include all author information and affiliations
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#0D9488] mt-1">•</span>
                Follow the journal's formatting guidelines
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#0D9488] mt-1">•</span>
                Ensure all references are properly cited
              </li>
            </ul>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
